import type { ReactElement } from 'react'

import { CompanyContact } from '../../../../../../elements/CompanyContact'
import { InvoicingSettingsLoadErrorMessage } from '../../../../../../elements/InvoicingSettingsLoadErrorMessage'
import { InvoicingSettingsSection } from '../../../../../../enums/invoicingSettingsSection'
import { useTrackInvoicingSettingsDebounced } from '../../../../../../hooks/useTrackInvoicingSettingsDebounced'
import { PresetsDocumentDefaults } from '../PresetsDocumentDefaults'
import { PresetsSettingsFormFieldsSkeletons } from './elements/PresetsSettingsFormFieldsSkeletons'

const section = InvoicingSettingsSection.Presets

interface PresetsSettingsFormFieldsProps {
  hasFailedToLoad: boolean
  isLoading: boolean
  onRetry: () => Promise<unknown>
}

// The fields stay hidden until the settings document has loaded. Showing them on a failed fetch
// would let an edit mark the form dirty, skip hydration, and save the empty defaults.
export const PresetsSettingsFormFields = ({
  hasFailedToLoad,
  isLoading,
  onRetry,
}: PresetsSettingsFormFieldsProps): ReactElement => {
  const trackEmailChange = useTrackInvoicingSettingsDebounced('E-mail address typed', { section })
  const trackFirstNameChange = useTrackInvoicingSettingsDebounced('First name typed', { section })
  const trackLastNameChange = useTrackInvoicingSettingsDebounced('Last name typed', { section })
  const trackPhoneChange = useTrackInvoicingSettingsDebounced('Phone number typed', { section })

  if (isLoading) {
    return <PresetsSettingsFormFieldsSkeletons />
  }

  if (hasFailedToLoad) {
    return <InvoicingSettingsLoadErrorMessage onRetry={onRetry} />
  }

  return (
    <>
      <PresetsDocumentDefaults />
      <CompanyContact
        onEmailChange={trackEmailChange}
        onFirstNameChange={trackFirstNameChange}
        onLastNameChange={trackLastNameChange}
        onPhoneChange={trackPhoneChange}
      />
    </>
  )
}
